class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems =
        this.closest('cart-items') ||
        this.closest('cart-drawer-items');

      if (cartItems) {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange);
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === 'cart-items') return;
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    const input = event.target;

    if (!input.matches('input[name="updates[]"]')) return;
    if (!input.dataset.index) return;

    this.updateQuantity(
      input.dataset.index,
      input.value,
      input.getAttribute('name')
    );
  }

  getSectionsToRender() {
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    const sections = [];

    if (items) {
      sections.push({
        id: 'main-cart-items',
        section: items.dataset.id || 'main-cart-items',
        selector: '.js-contents'
      });
    }

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      });
    }

    if (document.getElementById('cart-live-region-text')) {
      sections.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      });
    }

    if (footer) {
      sections.push({
        id: 'main-cart-footer',
        section: footer.dataset.id || 'main-cart-footer',
        selector: '.cart__blocks'
      });
    }

    return sections;
  }

  renderSections(sectionHTML, sections) {
    sections.forEach((section) => {
      const target = document.getElementById(section.id);
      const markup = sectionHTML?.[section.section];

      if (!target || typeof markup !== 'string') {
        throw new Error(
          `Warenkorbbereich fehlt: ${section.id}`
        );
      }

      const documentHTML = new DOMParser().parseFromString(
        markup,
        'text/html'
      );

      const source = documentHTML.querySelector(section.selector);
      const destination =
        target.querySelector(section.selector) || target;

      if (!source) {
        throw new Error(
          `Warenkorbinhalt fehlt: ${section.id}`
        );
      }

      destination.innerHTML = source.innerHTML;
    });
  }

  async onCartUpdate() {
    try {
      const sections = this.getSectionsToRender();
      const cartUrl = routes.cart_url || '/cart';

      const url = new URL(cartUrl, window.location.origin);
      url.searchParams.set(
        'sections',
        sections.map((section) => section.section).join(',')
      );

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('Warenkorb konnte nicht geladen werden.');
      }

      const sectionHTML = await response.json();
      this.renderSections(sectionHTML, sections);
    } catch (error) {
      console.error('Warenkorb-Aktualisierung:', error);
      window.location.reload();
    }
  }

  async updateQuantity(line, quantity, name) {
    const requestedQuantity = Number(quantity);
    const requestedLine = Number(line);

    if (
      !Number.isInteger(requestedQuantity) ||
      requestedQuantity < 0 ||
      !Number.isInteger(requestedLine) ||
      requestedLine < 1
    ) {
      return;
    }

    const quantityElement =
      document.getElementById(`Quantity-${line}`) ||
      document.getElementById(`Drawer-quantity-${line}`);

    const previousItemCount =
      this.querySelectorAll('.cart-item').length;

    this.enableLoading(line);

    try {
      const sections = this.getSectionsToRender();

      const body = JSON.stringify({
        line: requestedLine,
        quantity: requestedQuantity,
        sections: sections.map((section) => section.section),
        sections_url: window.location.pathname
      });

      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body
      });

      const state = await response.json();

      if (state.errors || !response.ok) {
        if (quantityElement) {
          quantityElement.value =
            quantityElement.getAttribute('value') || '1';
        }

        const errorMessage =
          typeof state.errors === 'string'
            ? state.errors
            : state.description ||
              window.cartStrings?.error ||
              'Die Menge konnte nicht geändert werden.';

        this.updateLiveRegions(line, errorMessage);
        return;
      }

      this.classList.toggle('is-empty', state.item_count === 0);

      const cartFooter =
        document.getElementById('main-cart-footer');
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartFooter) {
        cartFooter.classList.toggle(
          'is-empty',
          state.item_count === 0
        );
      }

      if (cartDrawer) {
        cartDrawer.classList.toggle(
          'is-empty',
          state.item_count === 0
        );
      }

      this.renderSections(state.sections, sections);

      let message = '';
      const updatedItem = state.items[requestedLine - 1];

      if (
        previousItemCount === state.items.length &&
        updatedItem &&
        updatedItem.quantity !== requestedQuantity
      ) {
        message = window.cartStrings?.quantityError
          ? window.cartStrings.quantityError.replace(
              '[quantity]',
              updatedItem.quantity
            )
          : `Die verfügbare Menge beträgt ${updatedItem.quantity}.`;
      }

      this.updateLiveRegions(line, message);

      const lineItem =
        document.getElementById(`CartItem-${line}`) ||
        document.getElementById(`CartDrawer-Item-${line}`);

      const input = lineItem?.querySelector(
        'input[name="updates[]"]'
      );

      if (input) {
        if (cartDrawer && typeof trapFocus === 'function') {
          trapFocus(cartDrawer, input);
        } else {
          input.focus();
        }
      } else if (
        state.item_count === 0 &&
        cartDrawer &&
        typeof trapFocus === 'function'
      ) {
        const emptyDrawer = cartDrawer.querySelector(
          '.drawer__inner-empty'
        );
        const link = cartDrawer.querySelector('a');

        if (emptyDrawer && link) {
          trapFocus(emptyDrawer, link);
        }
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: state
      });
    } catch (error) {
      console.error('Warenkorb-Mengenänderung:', error);

      // Falls die Aktualisierung der Anzeige fehlschlägt,
      // lädt die Seite den tatsächlichen Warenkorb neu.
      window.location.reload();
    } finally {
      this.disableLoading(line);
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);

    const errorText = lineItemError?.querySelector(
      '.cart-item__error-text'
    );

    if (errorText) {
      errorText.textContent = message;
    }

    const cartErrors =
      document.getElementById('cart-errors') ||
      document.getElementById('CartDrawer-CartErrors');

    if (cartErrors) {
      cartErrors.textContent = message;
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute(
        'aria-hidden',
        'true'
      );
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');

    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    const element = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);

    return element ? element.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');

    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    this.querySelectorAll(
      `#CartItem-${line} .loading-overlay,
       #CartDrawer-Item-${line} .loading-overlay`
    ).forEach((overlay) => {
      overlay.classList.remove('hidden');
    });

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute(
        'aria-hidden',
        'false'
      );
    }
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');

    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    this.querySelectorAll(
      `#CartItem-${line} .loading-overlay,
       #CartDrawer-Item-${line} .loading-overlay`
    ).forEach((overlay) => {
      overlay.classList.add('hidden');
    });
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({
              note: event.target.value
            });

            fetch(routes.cart_update_url, {
              ...fetchConfig(),
              body
            }).catch((error) => {
              console.error('Bestellnotiz:', error);
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

function handleDiscountForm(event) {
  event.preventDefault();

  const discountInput = event.target.querySelector(
    '[name="cart-discount-field"]'
  );

  const discountError = event.target.querySelector(
    '.cart-discount-form__error'
  );

  const discountValue = discountInput?.value.trim();

  if (!discountValue) {
    if (discountError) {
      discountError.style.display = 'block';
    }
    return;
  }

  if (discountError) {
    discountError.style.display = 'none';
  }

  const root = window.Shopify?.routes?.root || '/';

  window.location.href =
    `${root}checkout?discount=${encodeURIComponent(discountValue)}`;
}

function handleDiscountFormChange() {
  document.querySelectorAll(
    '.cart-discount-form__error'
  ).forEach((error) => {
    error.style.display = 'none';
  });
}